import React, { useCallback } from 'react';
import {
  Button,
  mergeClasses,
  Tooltip,
} from '@fluentui/react-components';

import { CollapseIcon, ExpandIcon } from './icons';
import { useStyles } from './styles';

interface CollapsibleSidePanelProps {
  classes?: {
    root?: string;
  };
  width?: string;
  title?: string | React.ReactNode;
  isCollapsed?: boolean;
  ariaLabel?: string;
  toolTipText?: string;
  setIsCollapsed: (isCollapsed: boolean) => void;
  children: React.ReactNode;
}

export const CollapsibleSidePanel: React.FC<CollapsibleSidePanelProps> = (props) => {
  const {
    width,
    title,
    isCollapsed,
    ariaLabel,
    toolTipText,
    setIsCollapsed,
    children,
  } = props;
  const classes = useStyles();
  const onToggleCollapse = useCallback(() => setIsCollapsed(!isCollapsed), [isCollapsed, setIsCollapsed]);

  const renderPanelButton = () => (
    <Button
      className={mergeClasses('collapsible-side-panel-icon-button', classes.collapseButton)}
      aria-label={ariaLabel}
      onClick={onToggleCollapse}
      icon={isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
      appearance="subtle"
    />
  );
  const renderPanelButtonWithToolTip = (toolTip) => (
    <Tooltip content={toolTip} relationship="label">
      {renderPanelButton()}
    </Tooltip>
  );

  const panelButton = toolTipText ? renderPanelButtonWithToolTip(toolTipText) : renderPanelButton();

  return (
    <div className={mergeClasses('collapsible-side-panel', classes.container, props.classes?.root)}>
      <div className={mergeClasses('collapsible-side-panel-header', classes.header)}>
        <h2
          className={mergeClasses('collapsible-side-panel-title', classes.title, isCollapsed && classes.collapsedTitle)}
          aria-hidden={isCollapsed ? 'true' : 'false'}
        >
          {title}
        </h2>
        {panelButton}
      </div>
      <div
        className={mergeClasses('collapsible-side-panel-content', classes.content, isCollapsed && classes.collapsedContent)}
        style={{ width: isCollapsed ? '0px' : width }}
        aria-hidden={isCollapsed ? 'true' : 'false'}
      >
        {children}
      </div>
    </div>
  );
};

CollapsibleSidePanel.defaultProps = {
  isCollapsed: false,
  width: '320px',
};
